import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  Tooltip,
  List,
  ListItemButton,
  ListItemText,
  Slider,
} from "@mui/material";
import { DisplaySettings as DisplaySettingsIcon } from "@mui/icons-material";
import {
  DataGridSettings,
  DEFAULT_SETTINGS,
  useDataGridSettings,
} from "./DataGridSettings";

interface DataGridSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

type SettingsPage = "display";

const NAV_ITEMS: { id: SettingsPage; label: string; icon: React.ReactNode }[] =
  [
    {
      id: "display",
      label: "Display",
      icon: <DisplaySettingsIcon fontSize="small" />,
    },
  ];

const ODD_ROW_PRESETS = [
  { label: "Blue-grey", color: "#e8edf5" },
  { label: "Sage", color: "#e8f0ea" },
  { label: "Lavender", color: "#ede8f5" },
  { label: "Peach", color: "#f5ede8" },
  { label: "Grey", color: "#f0f0f0" },
];

function DisplayPage({
  draft,
  setDraft,
}: {
  draft: DataGridSettings;
  setDraft: (d: DataGridSettings) => void;
}) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Compression */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Compression
        </Typography>

        <Box sx={{ mb: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.5,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Cell padding
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontFamily: "monospace" }}
            >
              {draft.cellPadding}px
            </Typography>
          </Box>
          <Slider
            value={draft.cellPadding}
            min={2}
            max={16}
            step={1}
            size="small"
            marks={[
              { value: 2, label: "2px" },
              { value: 16, label: "default" },
            ]}
            onChange={(_e, value) =>
              setDraft({ ...draft, cellPadding: value as number })
            }
          />
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={draft.stackColumnHeaders}
              onChange={(e) =>
                setDraft({ ...draft, stackColumnHeaders: e.target.checked })
              }
            />
          }
          label="Stack column header words"
        />
      </Box>

      <Divider />

      <FormControlLabel
        control={
          <Switch
            checked={draft.alternatingRows}
            onChange={(e) =>
              setDraft({ ...draft, alternatingRows: e.target.checked })
            }
          />
        }
        label="Alternating row colors"
      />

      {draft.alternatingRows && (
        <>
          <Divider />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Alternating row color
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <input
                type="color"
                value={draft.oddRowColor}
                onChange={(e) =>
                  setDraft({ ...draft, oddRowColor: e.target.value })
                }
                style={{
                  width: 36,
                  height: 28,
                  cursor: "pointer",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  padding: 2,
                  background: "none",
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontFamily: "monospace" }}
              >
                {draft.oddRowColor}
              </Typography>
            </Box>
            <Box
              sx={{ display: "flex", gap: 0.75, mt: 1.25, flexWrap: "wrap" }}
            >
              {ODD_ROW_PRESETS.map((preset) => (
                <Tooltip key={preset.label} title={preset.label}>
                  <Box
                    onClick={() =>
                      setDraft({ ...draft, oddRowColor: preset.color })
                    }
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: 0.75,
                      bgcolor: preset.color,
                      border:
                        draft.oddRowColor === preset.color
                          ? "2px solid #1976d2"
                          : "1px solid #ccc",
                      cursor: "pointer",
                      transition: "border-color 0.15s",
                      "&:hover": { borderColor: "#1976d2" },
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Base row color
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <input
                type="color"
                value={draft.evenRowColor}
                onChange={(e) =>
                  setDraft({ ...draft, evenRowColor: e.target.value })
                }
                style={{
                  width: 36,
                  height: 28,
                  cursor: "pointer",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  padding: 2,
                  background: "none",
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontFamily: "monospace" }}
              >
                {draft.evenRowColor}
              </Typography>
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Preview
            </Typography>
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              {[0, 1, 2, 3].map((i) => (
                <Box
                  key={i}
                  sx={{
                    height: 32,
                    bgcolor:
                      i % 2 === 0 ? draft.oddRowColor : draft.evenRowColor,
                    display: "flex",
                    alignItems: "center",
                    px: 1.5,
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    borderBottom: i < 3 ? "1px solid rgba(0,0,0,0.06)" : "none",
                  }}
                >
                  Row {i + 1}
                </Box>
              ))}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

export function DataGridSettingsModal({
  open,
  onClose,
}: DataGridSettingsModalProps) {
  const { settings, updateSettings } = useDataGridSettings();
  const [draft, setDraft] = useState<DataGridSettings>(settings);
  const [activePage, setActivePage] = useState<SettingsPage>("display");

  const handleEntered = () => setDraft({ ...settings });

  const needsReload =
    draft.stackColumnHeaders !== settings.stackColumnHeaders ||
    draft.cellPadding !== settings.cellPadding;

  const handleSave = () => {
    updateSettings(draft);
    if (needsReload) {
      window.location.reload();
    } else {
      onClose();
    }
  };

  const handleReset = () => setDraft({ ...DEFAULT_SETTINGS });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ transition: { onEntered: handleEntered } }}
    >
      <DialogTitle sx={{ pb: 0 }}>DataGrid Global Options</DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: "flex", minHeight: 360 }}>
          {/* Left nav */}
          <Box
            sx={{
              width: 160,
              flexShrink: 0,
              borderRight: "1px solid",
              borderColor: "divider",
              pt: 1,
            }}
          >
            <List dense disablePadding>
              {NAV_ITEMS.map((item) => (
                <ListItemButton
                  key={item.id}
                  selected={activePage === item.id}
                  onClick={() => setActivePage(item.id)}
                  sx={{
                    gap: 1,
                    "&.Mui-selected": {
                      bgcolor: "action.selected",
                      borderRight: "2px solid",
                      borderColor: "primary.main",
                    },
                  }}
                >
                  {item.icon}
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: "0.875rem" }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>

          {/* Right content */}
          <Box sx={{ flex: 1, p: 3, overflowY: "auto" }}>
            {activePage === "display" && (
              <DisplayPage draft={draft} setDraft={setDraft} />
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ borderTop: "1px solid", borderColor: "divider" }}>
        <Button onClick={handleReset} color="inherit" size="small">
          Reset to Defaults
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} variant="outlined" size="small">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" size="small">
          {needsReload ? "Save & Reload Page" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
